import { DocumentVersionDTO } from './document-version'
import { DocumentAccessDTO, DocumentAccessPayload } from './document-access'
import { CursorPageQuery, Nullable, SortQuery } from 'types/definitions'
import { WorkspaceScoped } from 'iam/dtos/workspace'
import { DocumentTypeDTO } from './document-type'

export interface DocumentDTO {
  /**
   * Document ID (integer).
   */
  id: string
  /**
   * Document long ID (GUID). Preferred identifier for GET endpoints
   * and any URL path param the backend auto-detects.
   */
  long_id: string
  /**
   * Date created.
   */
  date_created: string
  /**
   * Date modified, changes with meta updates or version update.
   */
  date_modified: string
  /**
   * Document title.
   */
  title: string
  /**
   * Optional description.
   */
  description?: string
  /**
   * The owner of the document (not necessarily the uploader).
   */
  owner_id: string
  /**
   * The public owner of the document (not necessarily the uploader).
   */
  owner_public_id: string
  /**
   * Workspace (e.g. `taxfyle`, `pwc`)
   */
  workspace_id: string
  /**
   * The latest version.
   */
  latest_version: DocumentVersionDTO
  /**
   * Is the document archived?
   */
  archived: boolean
  /**
   * When the document was archived, if it has been.
   */
  archived_date: string | null
  /**
   * Public ID of the user that archived the document, if it has been.
   */
  archived_by_user_public_id: string | null
  /**
   * Document type id.
   */
  document_type: Nullable<DocumentTypeDTO>
  /**
   * Access
   */
  access?: DocumentAccessDTO
  /**
   * Accesses.
   */
  accesses?: DocumentAccessDTO[]
  /**
   * History.
   */
  history?: DocumentVersionDTO[]

  /**
   * Shows if the file is temporary and will be deleted normally in an hour
   */
  ephemeral: boolean

  /**
   * Computed field determining whether the current user is allowed
   * to edit this document.
   */
  readonly can_edit: boolean
}

/**
 * Population options for document queries.
 */
export interface DocumentPopulate {
  history?: boolean
  accesses?: boolean
}

/**
 * Query for getting documents.
 */
// prettier-ignore
export interface GetDocumentsQueryBase extends DocumentPopulate, Partial<CursorPageQuery>, Partial<SortQuery<SearchDocumentsFields>>, Partial<WorkspaceScoped> {
  /**
   * If `true`, returns only archived documents. If `false`, returns only non-archived documents.
   * If left undefined, returns both.
   */
  archived?: boolean
}

/**
 * Get Job Documents query.
 */
export interface JobDocumentsQuery extends GetDocumentsQueryBase {
  context: DocumentAccessDTO['type']
  job_id: string
}

/**
 * Gets documents owned by the current user.
 */
export interface UserDocumentsQuery extends GetDocumentsQueryBase {
  context: 'User'
}

/**
 * Query for getting one or more documents.
 * The caller must provide a context to check permisssions against.
 * This is to avoid the API having to look up potentially thousands of jobs,
 * projects or accounts just to ensure we have permission.
 */
export type FindDocumentsQuery = JobDocumentsQuery | UserDocumentsQuery

/**
 * Fields that can be sorted by when searching documents.
 */
export const enum SearchDocumentsFields {
  Title = 'title',
  DateCreated = 'date_created',
  DateModified = 'date_modified',
}

/**
 * Query for searching documents.
 */
export interface SearchDocumentsQuery
  extends Omit<
    GetDocumentsQueryBase,
    keyof WorkspaceScoped | keyof DocumentPopulate
  > {
  owner_public_ids?: Array<string>
  workspace_ids?: Array<string>
  job_ids?: Array<string>
  document_type_ids?: Array<string>
  search_text?: string
}

export type ZipFilesPayload = Array<{
  document_id: string
  version_id: string
  file_id: string
}>

/**
 * Payload for creating a document.
 */
export interface CreateDocumentPayload extends WorkspaceScoped {
  title: string
  description?: string
  document_type_id?: string
  access?: DocumentAccessPayload
}

/**
 * Payload for updating a document
 */
export interface UpdateDocumentPayload extends Partial<WorkspaceScoped> {
  title?: string
  description?: string
}

export type ManualVerificationReason =
  | 'ClientNameMismatch'
  | 'MissingProSignature'

export interface FileDescriptor {
  id: string
  file_name: string
  date_created: string
  uploader_id: string
  state:
    | 'Downloadable'
    | 'Purged'
    | 'Scanning'
    | 'Pending'
    | 'Failed'
    | 'VerifyingClientData'
    | 'ManualVerificationRequired'
  manual_verification_reason?: ManualVerificationReason
}

export interface AssignDocumentTypePayload {
  type: string
}
